import React, { useRef, useState } from 'react';
// mui components
import { Box, Button, Typography } from '@mui/material';
// hooks
import { IArtistProfile } from 'hooks/swrRequests/useArtistProfiles';
// api
import api from 'utils/api';
// styles
import { colors } from 'styles/colors';
import { useTranslation } from 'react-i18next';

const GREEN = '#1F9D6B';
const MAX_BYTES = 10 * 1024 * 1024;
/** Mirrors the backend's BANKING_PROOF_ALLOWED_MIME_TYPES (documents + common image formats). */
const ACCEPTED_EXTENSIONS = [
  '.pdf',
  '.doc',
  '.docx',
  '.odt',
  '.rtf',
  '.txt',
  '.jpg',
  '.jpeg',
  '.png',
  '.webp',
  '.heic',
  '.heif',
  '.gif',
  '.bmp',
  '.tif',
  '.tiff',
];

interface OwnProps {
  profile: IArtistProfile;
  onUploaded: (updated: IArtistProfile) => void;
}

const BankingProofUpload: React.FC<OwnProps> = ({ profile, onUploaded }) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setUploading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const hasProof = !!profile.bankingProof?.fileName;

  const handleFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // allow re-selecting the same file
    event.target.value = '';
    if (!file) return;

    const extension = `.${file.name.split('.').pop()?.toLowerCase()}`;
    if (!ACCEPTED_EXTENSIONS.includes(extension)) {
      setErrorMessage(
        t(
          'Unsupported file type. Allowed: PDF, Word, ODT, RTF, TXT, JPG, PNG, WEBP, HEIC, GIF, BMP, TIFF'
        )
      );
      return;
    }
    if (file.size > MAX_BYTES) {
      setErrorMessage(t('The file is too large. Maximum size is 10 MB.'));
      return;
    }

    try {
      setErrorMessage(null);
      setUploading(true);
      const formData = new FormData();
      formData.append('file', file);
      const updated = await api.artistProfile.uploadBankingProof<IArtistProfile>({
        id: profile.id,
        data: formData,
      });
      onUploaded(updated);
    } catch (err: any) {
      const message = err?.data?.message;
      setErrorMessage(
        Array.isArray(message) ? message.join(', ') : message || t('Something went wrong')
      );
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box sx={{ pt: 1, pb: 1 }}>
      <Typography sx={{ fontSize: 16, color: colors.text.light.title, mb: 1 }}>
        {t('Banking Proof for bank account details')}{' '}
        <Box component="span" sx={{ color: colors.system.error }}>
          *
        </Box>
      </Typography>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_EXTENSIONS.join(',')}
        style={{ display: 'none' }}
        onChange={handleFile}
      />
      <Button
        variant="outlined"
        disabled={isUploading}
        onClick={() => inputRef.current?.click()}
        sx={{
          textTransform: 'none',
          fontSize: 13,
          px: 2,
          py: 1.1,
          borderStyle: hasProof ? 'solid' : 'dashed',
          borderColor: hasProof ? GREEN : '#CFCFCF',
          color: hasProof ? GREEN : '#6B6B6B',
          bgcolor: hasProof ? '#EAF7F1' : '#FAFAFA',
          '&:hover': {
            borderColor: hasProof ? GREEN : colors.main.purple,
            bgcolor: hasProof ? '#EAF7F1' : '#FAFAFA',
          },
        }}
      >
        {isUploading
          ? t('Uploading…')
          : hasProof
          ? `✓ ${profile.bankingProof?.fileName}`
          : `↑ ${t('Upload statement or bank letter')}`}
      </Button>
      {errorMessage && (
        <Typography sx={{ fontSize: 16, color: colors.system.error, mt: 0.7 }}>
          {errorMessage}
        </Typography>
      )}
    </Box>
  );
};

export default BankingProofUpload;
